// Object-space position from vertex shader.
varying vec3 v_objectPos;

// Dark-mode / glow shared with flush pockets
uniform float u_isDark;
uniform vec3  u_glowColor;
uniform float u_emissiveStrength;

// ── Groove ──────────────────────────────────────────────────────────────────
// A circular torus-section channel at the die wall.
// Mask is 1 inside the carved groove, 0 outside.
uniform float u_grooveEnabled;   // 0.0 | 1.0
uniform float u_coreRadius;      // ring centre-line radius (scene units)
uniform float u_grooveZ;         // Z of groove torus centre
uniform float u_grooveTube;      // tube radius (half channel width)

// ── Sine-wave band ───────────────────────────────────────────────────────────
// An undulating stripe that oscillates in Z as a function of angle around the ring.
uniform float u_waveEnabled;     // 0.0 | 1.0
uniform float u_waveAmplitude;   // Z amplitude (scene units)
uniform float u_waveFrequency;   // complete oscillations per full revolution
uniform float u_waveBand;        // stripe half-width in Z (scene units)

// ── Gem setting pockets ──────────────────────────────────────────────────────
// Painted directly on the ring surface at each gem's object-space position.
// No separate mesh — the "pocket" is a shader-driven disc on the ring body.
#define MAX_GEMS 32
uniform int   u_gemCount;
uniform vec3  u_gemPositions[MAX_GEMS];
uniform float u_gemRadii[MAX_GEMS];  // per-gem: gem.diameter/2 + 0.1mm in scene units

void main() {
    float radial = length(v_objectPos.xy);

    // ── Groove mask ──────────────────────────────────────────────────────────
    // Distance from the (coreRadius, grooveZ) torus centre in the (radial, Z) plane.
    float dx = radial - u_coreRadius;
    float dz = v_objectPos.z - u_grooveZ;
    float grooveDist  = length(vec2(dx, dz));
    float grooveMask  = (1.0 - smoothstep(u_grooveTube * 0.5, u_grooveTube, grooveDist))
                        * u_grooveEnabled;

    // ── Sine-wave mask ───────────────────────────────────────────────────────
    // theta in [-PI, PI]; waveZ is the Z centreline of the stripe at this angle.
    float theta       = atan(v_objectPos.y, v_objectPos.x);
    float waveZ       = u_waveAmplitude * sin(theta * u_waveFrequency);
    float waveMask    = (1.0 - smoothstep(u_waveBand * 0.4, u_waveBand, abs(v_objectPos.z - waveZ)))
                        * u_waveEnabled;

    float patternMask = max(grooveMask, waveMask);

    // ── Gem pocket mask ──────────────────────────────────────────────────────
    // For each gem, test 3D distance from the ring surface fragment to the gem
    // centre (both in local object space). This naturally produces a circular
    // disc only on the outer surface — inner bore and side faces are too far away.
    //
    // gemMask – filled disc that darkens/roughens the carved socket under the gem.
    // The previous emissive halo around the gem was removed: the gem material now
    // emits its own glow from inside the setting (see createGemMaterial), so the
    // ring surface must not paint a competing glow ring around it.
    float gemMask = 0.0;
    for (int i = 0; i < MAX_GEMS; i++) {
        if (i >= u_gemCount) break;
        float d = length(v_objectPos - u_gemPositions[i]);
        float r = u_gemRadii[i];
        // Pocket disc
        float m = 1.0 - smoothstep(r * 0.75, r, d);
        gemMask = max(gemMask, m);
    }

    // Pocket surface: slightly rougher, less metallic — looks like a carved socket.
    csm_Roughness  = mix(csm_Roughness,  0.7, gemMask);
    csm_Metalness  = mix(csm_Metalness,  0.3, gemMask);
    csm_DiffuseColor.rgb = mix(csm_DiffuseColor.rgb, csm_DiffuseColor.rgb * 0.65, gemMask);

    // Pattern surface: groove/wave carve a slightly darker, less metallic channel —
    // visible in both light and dark mode (same "shadowy" logic as gem pockets).
    csm_Roughness  = mix(csm_Roughness,  0.55, patternMask);
    csm_Metalness  = mix(csm_Metalness,  0.5,  patternMask);
    csm_DiffuseColor.rgb = mix(csm_DiffuseColor.rgb, csm_DiffuseColor.rgb * 0.72, patternMask);

    // Emissive: groove/wave patterns glow in dark mode. The gem itself emits its
    // own glow (createGemMaterial dark-mode emissive) — no halo is painted here.
    csm_Emissive  = u_glowColor * (u_emissiveStrength * u_isDark * patternMask);
}
